import { WAIT_REASON_WORDS } from '../renderer/onboarding';
import { linesMissTheirTotal, money } from '../shared/line-item-arithmetic';
import type { EngineCall } from './desktop-commands';
import {
  fieldConfidenceWords,
  type ConfidenceDocument,
  type ConfidencePolicy,
  type ConfidenceSchema,
} from './field-confidence-holds';
import { fieldProblemWords, type ProblemDocument } from './field-problem-words';
import { sendsToWords } from './exported-once';

/**
 * PAIGE: a document Paige already exported that a later check would hold (the engine's
 * `flaggedAfterExport`, DocumentStore.LedgerReleases.cs). It never goes back to Needs review: a
 * startup re-settle, a new rule or a raised threshold used to put exported documents back in the
 * list as new work, and a routine Confirm sent them out again (2026-10-05). The screen says what
 * it is, marks what the check points at, and gives a way to reach these documents. Once exported,
 * Paige is done with a document (ADR-0031, 2026-10-10): correcting and confirming it teaches
 * Cortexa and sends nothing again, and the Confirm's hint says so.
 */

/**
 * PAIGE: the same seam carries what a regrouping left for a person (2026-10-06, the owner's
 * decision: a split, a join, a removed or a restored page of an exported document sends nothing;
 * since 2026-10-10 a Confirm does not either, ADR-0031). Whether an exported copy differs from the document on screen is the ledger's to
 * say, so the API adds its documents to the list under reasons of their own
 * (services/Paige.Api/Engine/RegroupedAfterExport.cs); the engine's document never carries them.
 */
export const REGROUPED_WORDS: Readonly<Record<string, readonly [lead: string, rest: string]>> = {
  'exported-copy-differs': [
    'Already exported.',
    'The exported copy has other pages than this document. It is not sent again.',
  ],
  'exported-values-differ': [
    'Already exported.',
    'The exported copy has other values. Your corrections teach Cortexa; it is not sent again.',
  ],
  'exported-pages-moved': [
    'Pages already exported',
    'in another document. Confirming sends this one only if it holds a page that never went out.',
  ],
  'regrouped-before-export': ['Regrouped before it was exported.', 'Confirming sends it.'],
};

/** The ledger's reason for each listed document a regrouping left for a person. */
export function regroupedReasons(listed: readonly FlaggedDocument[]): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const flag of listed) {
    const reason = flag.reasons.find((candidate) => candidate in REGROUPED_WORDS);
    if (reason) reasons.set(flag.documentId, reason);
  }
  return reasons;
}

/** One flagged document as the host lists it (`list-flagged-after-export`). */
export interface FlaggedDocument {
  batchId: string;
  documentId: string;
  reasons: string[];
  /** The ledger's: destinations that refused it (reason `exported-refused`), by the customer's names. */
  refusedAt?: string[];
  /** The ledger's: destinations it has not reached yet, which a corrected Confirm also sends to. */
  notYet?: string[];
}

/** What a Confirm of each listed document a destination refused will do, in the screen's words (ADR-0031). */
export function refusedWords(listed: readonly FlaggedDocument[]): Map<string, string> {
  const words = new Map<string, string>();
  for (const flag of listed) {
    const line = sendsToWords(flag.refusedAt, flag.notYet);
    if (line) words.set(flag.documentId, line);
  }
  return words;
}

/** What the words and marks need of a document. */
export type FlaggedView = ConfidenceDocument &
  ProblemDocument & {
    id?: string;
    flaggedAfterExport?: readonly string[] | null;
    pages?: readonly unknown[] | null;
    lineItems?: readonly { amount?: number | null; verified?: boolean }[] | null;
  };

type TotalsFields = Parameters<typeof linesMissTheirTotal>[1];

/** What a change to the open document's values or pages looks like: the list is read again when it moves. */
export function contentStamp(open: FlaggedView | null | undefined): string {
  const values = (open?.fields ?? []).map((field) => (field as { value?: unknown }).value ?? '');
  const rows = (open?.lineItems ?? []).map((row) => row.amount ?? '');
  return [open?.pages?.length ?? '', values.join('|'), rows.join('|')].join(':');
}

/** The adapter's method: the job's flagged documents in one batch. */
export function flaggedCommands(call: EngineCall): Record<string, unknown> {
  return {
    listFlaggedAfterExport: (request: { jobId: string; batchId?: string }) =>
      call<FlaggedDocument[]>('list-flagged-after-export', {
        jobId: request.jobId,
        ...(request.batchId ? { batchId: request.batchId } : {}),
      }),
  };
}

/** The reasons the later check gives; empty for a document it does not flag. */
export function flaggedReasons(document: FlaggedView | null | undefined): readonly string[] {
  if (!document || document.confirmedAsWhole) return [];
  return document.flaggedAfterExport ?? [];
}

/**
 * The document as the later check sees it: held, for the flag's reasons. The screen's own marks
 * (the checklist, the field rows) are asked with this, so the value the check doubts is pointed
 * at the way a held document's is. A document that is not flagged is answered as it is.
 */
export function asTheCheckSeesIt<T extends FlaggedView>(document: T): T {
  const reasons = flaggedReasons(document);
  if (reasons.length === 0 || document.needsReview) return document;
  return { ...document, needsReview: true, waitReasons: reasons };
}

/** Each reason in the screen's own plain words, most specific first; never more than three lines. */
export function flaggedReasonWords(
  document: FlaggedView,
  schema: ConfidenceSchema,
  policy: ConfidencePolicy,
): string[] {
  const reasons = flaggedReasons(document);
  const named = reasons.filter((reason) => reason !== 'problem' && reason !== 'review');
  const words = (named.length > 0 ? named : reasons.slice(0, 1))
    .map((reason) => reasonWords(reason, document, schema, policy))
    .filter((line, index, all) => all.indexOf(line) === index)
    .map(sentence);
  return words.length > 3 ? [...words.slice(0, 2), `And ${words.length - 2} more`] : words;
}

function reasonWords(
  reason: string,
  document: FlaggedView,
  schema: ConfidenceSchema,
  policy: ConfidencePolicy,
): string {
  if (reason === 'field-confidence')
    return fieldConfidenceWords(asTheCheckSeesIt(document), schema, policy);
  if (reason === 'field-problem') return fieldProblemWords(document);
  if (reason === 'table-total') return tableTotalWords(document) ?? WAIT_REASON_WORDS[reason];
  return WAIT_REASON_WORDS[reason] ?? WAIT_REASON_WORDS['problem'];
}

/** The table chip's own words (LineItemArithmeticStatus): "Lines 59.80 short of the subtotal". */
function tableTotalWords(document: FlaggedView): string | null {
  const totals = linesMissTheirTotal(
    document.lineItems ?? [],
    (document.fields ?? []) as TotalsFields,
  );
  if (!totals) return null;
  if (!totals.subtotal) return 'Lines run past the total';
  const gap = money(Math.abs(totals.sum - totals.total));
  return `Lines ${gap} ${totals.sum < totals.total ? 'short of' : 'over'} the subtotal`;
}

const sentence = (words: string): string => words.charAt(0).toUpperCase() + words.slice(1);

/**
 * The main button's words (CapturePoint's single confirm: "Confirm & next", "Confirm"). The label is
 * the same on an exported document; its hint says that confirming sends nothing again (ADR-0031).
 */
export function confirmWords(
  exported: boolean,
  label: string,
  sendsTo?: string | null,
): { label: string; tooltip: string | undefined } {
  if (sendsTo) return { label, tooltip: sendsTo };
  return exported
    ? { label, tooltip: 'Already exported: confirming teaches Cortexa and sends nothing again' }
    : { label, tooltip: undefined };
}

/** The flagged document after `current` in the list's order, wrapping; null when there is none. */
export function nextFlagged(
  documents: readonly { id: string }[],
  flagged: ReadonlySet<string>,
  current: number,
): number | null {
  const indexes = documents.flatMap((document, index) => (flagged.has(document.id) ? [index] : []));
  return indexes.find((index) => index > current) ?? indexes[0] ?? null;
}

/**
 * The flagged documents of the open batch: the host's list, corrected by the documents the screen
 * holds in full (`loaded`), whose own flag is newer (a document just confirmed has lost it). A
 * document the ledger listed as regrouped stays: the engine's document does not carry that.
 */
export function flaggedIds(
  listed: readonly FlaggedDocument[],
  documents: readonly FlaggedView[],
  loaded: ReadonlySet<string>,
): Set<string> {
  const ids = new Set(listed.map((flag) => flag.documentId));
  const regrouped = regroupedReasons(listed);
  for (const id of refusedWords(listed).keys()) regrouped.set(id, 'exported-refused');
  const held = new Set<string>();
  for (const document of documents) {
    if (!document.id) continue;
    held.add(document.id);
    if (!loaded.has(document.id)) continue;
    if (flaggedReasons(document).length > 0) ids.add(document.id);
    else if (!regrouped.has(document.id)) ids.delete(document.id);
  }
  return new Set([...ids].filter((id) => held.has(id)));
}
